import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { ViewportScroller } from "@angular/common";
import { Component, HostListener, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatDrawer, MatSidenavContent } from "@angular/material/sidenav";
import { Event as RouterEvent, NavigationEnd, Router, Scroll } from "@angular/router";
import { Observable, timer } from "rxjs";
import { filter, map, shareReplay, take } from "rxjs/operators";
import { AuthService } from "../auth/auth.service";
import { UserManagementPermissions } from "../shared/permissions/user-management-permissions";
import { UserService } from "../shared/services/user.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { VersionService } from "../shared/services/version.service";
import { NotificationService } from "../shared/services/notification.service";
import { AdminPermissions } from "../shared/permissions/admin-permissions";
import { AdministrativeUnit } from "../shared/models/organizations/administrative-unit";
import { AppEvents } from "../shared/app-events";
import { LetterManagerPermissions } from "../shared/permissions/letter-manager-permissions";
import { EmailLogPermissions } from "../shared/permissions/email-log-permissions";
import { TextBlockManagerPermissions } from "../shared/permissions/text-block-manager-permissions";
import { ContactManagerPermissions } from "../shared/permissions/contact-manager-permissions";
import { SppIndicatorManagerPermissions } from "../shared/permissions/spp-indicator-manager-permissions";
import { SurveyManagerPermissions } from "../shared/permissions/survey-manager-permissions";
import { CompliancePermissions } from "src/app/shared/permissions/compliance-permissions";
import { DisputeResolutionPermissions } from "src/app/shared/permissions/dispute-resolution-permissions";
import { FiscalPermissions } from "src/app/shared/permissions/fiscal-permissions";
import { IndicatorSummaryPermissions } from "src/app/shared/permissions/indicator-summary-permissions";
import { ParentSurveyPermissions } from "src/app/shared/permissions/parent-survey-permissions";
import { PostSchoolOutcomesPermissions } from "src/app/shared/permissions/postSchool-outcomes-permissions";
import { DataListPermissions } from "src/app/shared/permissions/data-list-permissions";
import { PersonnelStatusPermissions } from "src/app/shared/permissions/personnel-status-permissions";
import { DataListService } from "../shared/services/data-list.service";
import { SchoolYearRepository } from "../shared/repositories/schoolyear.repository";
import { ReportType } from "../shared/models/reports";
import { GiftedTalentedFiscalPermissions } from "../shared/permissions/giftedtalented-fiscal-permissions";
import { FacilitatedAssessmentPermissions } from "../shared/permissions/facilitated-assessment-permissions";
import { StateComplaintPermissions } from "../shared/permissions/state-complaint-permissions";
import { DueProcessPermissions } from "../shared/permissions/due-process-permissions";
import { MediationPermissions } from "../shared/permissions/mediation-permissions";
import { ParentEngagementPermissions } from "../shared/permissions/parent-engagement-permissions";
import { AreaOfConcernPermissions } from "../shared/permissions/area-of-concern-permissions";
import { FacilitationPermissions } from "../shared/permissions/facilitation-permissions";

@UntilDestroy(this)
@Component({
  selector: "app-nav",
  templateUrl: "./nav.component.html",
  styleUrls: ["./nav.component.scss"],
  standalone: false,
})
export class NavComponent implements OnInit {
  // @HostListener("window:keyup.tab", ["$event"])
  // @HostListener("window:keydown.tab", ["$event"])
  // keyEvent(event: KeyboardEvent) {
  //   if (event.target["ariaLabel"] === "Corrections") {
  //     setTimeout(() => (this.isCorrectionsSectionExpanded = true));
  //   }
  //   if (event.target["ariaLabel"] === "Gifted") {
  //     setTimeout(() => (this.isGiftedSectionExpanded = true));
  //   }
  //   if (event.target["ariaLabel"] === "Reports") {
  //     setTimeout(() => (this.isReportsSectionExpanded = true));
  //   }
  //   if (event.target["ariaLabel"] === "Administration") {
  //     setTimeout(() => (this.isAdminSectionExpanded = true));
  //   }
  //   if (event.target["ariaLabel"] === "Help") {
  //     setTimeout(() => (this.isHelpSectionExpanded = true));
  //   }
  // }

  goToSchoolYearSelection() {
    // Focus on the school year selector in the top navigation bar
    setTimeout(() => {
      // Check if school year selector is visible (not hidden by hideForRoute())
      const schoolYearContainer = document.querySelector('#schoolYearSelector') as HTMLElement;
      if (schoolYearContainer && !schoolYearContainer.closest('[hidden]')) {
        const schoolYearSelector = schoolYearContainer.querySelector('mat-select') as HTMLElement;
        if (schoolYearSelector) {
          schoolYearSelector.focus();
          return;
        }
      }

      // Fallback 1: Focus on the main content area
      const mainContent = document.querySelector('.main-content') as HTMLElement;
      if (mainContent) {
        mainContent.setAttribute('tabindex', '-1');
        mainContent.focus();
        // Remove tabindex after focus to not interfere with natural tab order
        setTimeout(() => mainContent.removeAttribute('tabindex'), 100);
        return;
      }

      // Fallback 2: Focus on the page header if it exists
      const pageHeader = document.querySelector('.page-header') as HTMLElement;
      if (pageHeader) {
        pageHeader.setAttribute('tabindex', '-1');
        pageHeader.focus();
        setTimeout(() => pageHeader.removeAttribute('tabindex'), 100);
      }
    }, 100);
  }

  private handlePostNavigationFocus() {
    // Only manage focus if navigation was triggered by menu interaction.
    // This prevents interfering with programmatic navigation or browser back/forward.
    setTimeout(() => {
      const activeElement = document.activeElement;
      const isFromNavigation = activeElement &&
        (activeElement.closest('mat-nav-list') ||
         activeElement.closest('.sidenav') ||
         activeElement.getAttribute('routerLink') !== null);

      if (isFromNavigation) {
        this.goToSchoolYearSelection();
      }
    }, 150);
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: Event) {
    // Allow users to return focus to navigation with Escape key
    const activeElement = document.activeElement as HTMLElement;
    const isInMainContent = activeElement && activeElement.closest('.main-content');

    if (isInMainContent) {
      this.goToSchoolYearSelection();
      event.preventDefault();
    }
  }

  @ViewChild("drawer") drawer: MatDrawer;
  scrollPosition: number;
  version = "";
  pageFirstTabIndex: number = 1300;
  globalFirstTabIndex: number = 100;

  /*
  // Moved to ngOnInit to avoid initialization error.
  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay()
    );
  */
  isHandset$: Observable<boolean>;

  @ViewChild("sideNavContent") mainContent: MatSidenavContent;

  drawerOpen = true;

  userFullName = "";
  userEmail = "";
  isMobile: boolean;

  UserManagementPermissions = UserManagementPermissions;
  AdminPermissions = AdminPermissions;
  LetterManagerPermissions = LetterManagerPermissions;
  EmailLogPermissions = EmailLogPermissions;
  TextBlockManagerPermissions = TextBlockManagerPermissions;
  ContactManagerPermissions = ContactManagerPermissions;
  SppIndicatorManagerPermissions = SppIndicatorManagerPermissions;
  SurveyManagerPermissions = SurveyManagerPermissions;
  CompliancePermissions = CompliancePermissions;
  DisputeResolutionPermissions = DisputeResolutionPermissions;
  FiscalPermissions = FiscalPermissions;
  IndicatorSummaryPermissions = IndicatorSummaryPermissions;
  ParentSurveyPermissions = ParentSurveyPermissions;
  PostSchoolOutcomesPermissions = PostSchoolOutcomesPermissions;
  DataListPermissions = DataListPermissions;
  PersonnelStatusPermissions = PersonnelStatusPermissions;
  GiftedTalentedFiscalPermissions = GiftedTalentedFiscalPermissions;

  isAu = false;
  isState = false;
  isPublic = false;
  hasSomeAdminFeature = false;
  isUserEdit = false;
  isViewEmailLogs = false;
  isEditLetters = false;
  isEditTextBlock = false;
  isEditContact = false;
  isEditTags = false;
  isEditSPP = false;
  isEditSurvey = false;
  isEditDataList = false;
  isViewPersonnelStatus = false;
  isEditPersonnelStatus = false;
  showAuTasks = false;
  isViewPSO = false;
  isEditPSOData = false;
  isViewParentSurveyData = false;
  isEditParentSurveyData = false;
  showCorrections = false;
  isCorrections = false;
  showGiftedDashboard = false;
  showGiftedMonitoring = false;
  isFiscal = false;
  showFiscal = false;
  isViewFacilitatedAssessment = false;
  isEditFacilitatedAssessment = false;
  showFacilitatedAssessment = false;

  showStateComplaint = false;
  isStateComplaint = false;
  showDueProcess = false;
  isDueProcess = false;
  showMediation = false;
  isMediation = false;
  showParentEngagement = false;
  isParentEngagement = false;
  showEarlyDisputeResolution = false;
  isEarlyDisputeResolution = false;
  showAreaOfConcern = false;
  isAreaOfConcern = false;
  showIEPFacilitation = false;
  isIEPFacilitation = false;

  currentAdministrativeUnit: string;
  userAdministrativeUnits: AdministrativeUnit[] = [];
  schoolYears: string[] = [];
  currentSchoolYear: string;
  urlInfo: string;

  routesToHideNav: string[] = ["/documents$", "/students$", "/files$"];

  ReportType: ReportType;

  isReportsSectionExpanded = false;
  isStateComplaintSectionExpanded = false;
  isDueProcessSectionExpanded = false;
  isMediationSectionExpanded = false;
  isParentEngagementSectionExpanded = false;
  isEarlyDisputeResolutionSectionExpanded = false;
  isAreaOfConcernSectionExpanded = false;
  isIEPFacilitationSectionExpanded = false;
  isAdminSectionExpanded = false;
  isCorrectionsSectionExpanded = false;
  isHelpSectionExpanded = false;

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public authService: AuthService,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller,
    // private helpService: HelpService,
    private dialog: MatDialog,
    private userService: UserService,
    private versionService: VersionService,
    private notificationService: NotificationService,
    private datalistService: DataListService,
    private schoolYearRepo: SchoolYearRepository
  ) {
    this.router.events
      .pipe(
        filter((e: RouterEvent): e is NavigationEnd => e instanceof NavigationEnd)
      )
      .subscribe((e) => {
        this.expandNavigationSections(e["url"]);
        // Manage focus after navigation completes (menu -> page content)
        this.handlePostNavigationFocus();
      });
  }

  ngOnInit() {
    this.isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      map((result) => result.matches),
      shareReplay()
    );

    this.router.events
      .pipe(filter((e: RouterEvent): e is Scroll => e instanceof Scroll))
      .subscribe((e) => {
        if (e.position) {
          // backward navigation
          this.viewportScroller.scrollToPosition(e.position);
        } else if (e.anchor) {
          // anchor navigation
          this.viewportScroller.scrollToAnchor(e.anchor);
        } else {
          // forward navigation
          this.mainContent.getElementRef().nativeElement.scrollTo(0, 0);
        }
      });

    // eslint-disable-next-line no-extra-boolean-cast
    if (!!this.authService.authUser) {
      this.authService.authUserChanged$.subscribe(() => {
        if (
          this.authService.authUser.firstName &&
          this.authService.authUser.lastName
        ) {
          this.userFullName = `${this.authService.authUser.firstName} ${this.authService.authUser.lastName}`;
        }
        this.userEmail = this.authService.authUser.email;
        this.isAu = this.authService.isAdministrativeUnitUser;
        this.isState = this.authService.isStateEducationAgencyUser;
        this.isPublic = this.authService.isPublicUser;
        this.currentAdministrativeUnit = this.authService
          .administrativeUnitId as string;

        //figure out what permissions the user has so we can show the correct menu items
        this.isUserEdit = this.authService.getPermission(
          UserManagementPermissions.Edit
        );
        this.isViewEmailLogs = this.authService.getPermission(
          EmailLogPermissions.View
        );
        this.isEditLetters = this.authService.getPermission(
          LetterManagerPermissions.Edit
        );
        this.isEditTextBlock = this.authService.getPermission(
          TextBlockManagerPermissions.Edit
        );
        this.isEditContact = this.authService.getPermission(
          ContactManagerPermissions.Edit
        );
        this.isEditSPP = this.authService.getPermission(
          SppIndicatorManagerPermissions.Edit
        );
        this.isEditSurvey = this.authService.getPermission(
          SurveyManagerPermissions.Edit
        );
        this.isEditDataList = this.authService.getPermission(
          DataListPermissions.Edit
        );
        this.isViewPersonnelStatus = this.authService.getPermission(
          PersonnelStatusPermissions.View
        );
        this.isEditPersonnelStatus = this.authService.getPermission(
          PersonnelStatusPermissions.Edit
        );
        this.isViewPSO = this.authService.getPermission(
          PostSchoolOutcomesPermissions.View
        );
        this.isEditPSOData = this.authService.getPermission(
          PostSchoolOutcomesPermissions.EditExitInputData
        );
        this.isCorrections = this.authService.getPermission(
          CompliancePermissions.View
        );
        this.isViewParentSurveyData = this.authService.getPermission(
          ParentSurveyPermissions.View
        );
        this.isEditParentSurveyData = this.authService.getPermission(
          ParentSurveyPermissions.EditDataInput
        );
        this.isFiscal = this.authService.getPermission(FiscalPermissions.View);
        this.isViewFacilitatedAssessment = this.authService.getPermission(
          FacilitatedAssessmentPermissions.View
        );
        this.isEditFacilitatedAssessment = this.authService.getPermission(
          FacilitatedAssessmentPermissions.Edit
        );

        this.isStateComplaint = this.authService.getPermission(
          StateComplaintPermissions.View
        );
        this.isDueProcess = this.authService.getPermission(
          DueProcessPermissions.View
        );
        this.isMediation = this.authService.getPermission(
          MediationPermissions.View
        ) || this.authService.getPermission(MediationPermissions.ViewSummary);
        this.isParentEngagement = this.authService.getPermission(
          ParentEngagementPermissions.View
        );
        this.isEarlyDisputeResolution = this.authService.getPermission(
          ParentEngagementPermissions.View
        );
        this.isAreaOfConcern = this.authService.getPermission(
          AreaOfConcernPermissions.View
        );
        this.isIEPFacilitation = this.authService.getPermission(
          FacilitationPermissions.View
        ) || this.authService.getPermission(FacilitationPermissions.ViewSummary);

        //check if any admin permissions to know if to show the Admin menu parent item
        this.hasSomeAdminFeature =
          this.isUserEdit ||
          this.isViewEmailLogs ||
          this.isEditLetters ||
          this.isEditTextBlock ||
          this.isEditContact ||
          this.isEditSPP ||
          this.isEditSurvey ||
          this.isEditDataList;

        //check to see if we show the Compliance Menu item
        this.showCorrections =
          this.isCorrections ||
          this.isViewPersonnelStatus ||
          this.isEditPersonnelStatus;

        //check to see if we show the AUTasks menu item
        this.showAuTasks =
          this.isViewPSO ||
          this.isEditPSOData ||
          this.isViewParentSurveyData ||
          this.isEditParentSurveyData;

        this.showStateComplaint = this.isStateComplaint;
        this.showDueProcess = this.isDueProcess;
        this.showMediation = this.isMediation;
        this.showParentEngagement = this.isParentEngagement;
        this.showEarlyDisputeResolution = this.isEarlyDisputeResolution && !this.isPublic;
        this.showAreaOfConcern = this.isAreaOfConcern && !this.isPublic;
        this.showIEPFacilitation = this.isIEPFacilitation;
      });

      this.showFiscal = this.isFiscal;

      this.showFacilitatedAssessment =
        this.isViewFacilitatedAssessment || this.isEditFacilitatedAssessment;

      this.authService.publishAuthUserChanged();

      this.isHandset$.subscribe((d: boolean) => {
        this.isMobile = d;

        if (d) {
          this.drawerOpen = false;
        }
      });
    }

    this.userService.getUserAdministrativeUnits().subscribe((r) => {
      this.userAdministrativeUnits = r;
    });

    this.datalistService
      .getSchoolYears({ filter: {}, pageIndex: 0, pageSize: 10 })
      .subscribe((r) => {
        this.schoolYears = r
          .filter((f) => f.isActive == true)
          .map((m) => m.label);

        //const current = r.find(
        //  (f) => f.isCurrent == true && f.isActive == true
        //);

        const current = r.find(
          (f) => f.isActive == true && f.label === this.authService.schoolYear
        );

        this.currentSchoolYear = current ? current.label : this.schoolYears[0];

        this.schoolYearRepo.setSelectedSchoolYear(this.currentSchoolYear);
        this.schoolYearRepo.setSchoolYears(this.schoolYears);
      });

    this.versionService.currentVersion
      .pipe(untilDestroyed(this))
      .subscribe((v) => {
        if (!!this.version && this.version !== v) {
          this.notificationService.openSnackBar(
            "A new version of RESOLVED DR is available. Refresh your page to see it.",
            null,
            null,
            10000
          );
        }
        this.version = v;
      });
  }

  expandNavigationSections(urlInfo) {
    this.isReportsSectionExpanded = urlInfo?.includes("/reports/") || false;

    this.isStateComplaintSectionExpanded =
      urlInfo?.includes("/state-complaint/") || false;
    this.isDueProcessSectionExpanded =
      urlInfo?.includes("/due-process/") || false;
    this.isMediationSectionExpanded = urlInfo?.includes("/mediation/") || false;
    this.isParentEngagementSectionExpanded =
      urlInfo?.includes("/parent-engagement/") || false;
    this.isEarlyDisputeResolutionSectionExpanded =
      urlInfo?.includes("/early-dispute-resolution/") || false;
    this.isAreaOfConcernSectionExpanded =
      urlInfo?.includes("/area-of-concern/") || false;
    this.isIEPFacilitationSectionExpanded =
      urlInfo?.includes("/facilitation/") || false;

    this.isAdminSectionExpanded = urlInfo?.startsWith("/admin") || false;
    this.isCorrectionsSectionExpanded =
      urlInfo?.includes("/corrections/") || false;
  }

  hideForRoute() {
    let hide = false;

    this.routesToHideNav.forEach((x) => {
      if (!hide) {
        const regexp = new RegExp(x);
        hide = regexp.test(this.router.url);
      }
    });

    return hide;
  }

  toggleDrawer() {
    this.drawer.toggle();
    this.drawerOpen = this.drawer.opened;
  }

  logout() {
    this.authService.logout();
  }

  onScroll(e) {
    this.scrollPosition = e.target.scrollTop;
  }

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    if (!this.drawer) {
      return;
    }
    timer(50)
      .pipe(take(1))
      .subscribe(() => (this.drawerOpen = this.drawer.opened));
  }

  // onOpenHelp() {
  //  //this.helpService.helpModal();
  // }

  administrativeUnitChanged() {
    this.authService
      .selectAdministrativeUnit(
        this.currentAdministrativeUnit,
        this.currentSchoolYear
      )
      .subscribe((_) => {
        window.location.href = "/";
      });
  }

  schoolYearChanged() {
    this.authService
      .selectAdministrativeUnit(
        this.currentAdministrativeUnit,
        this.currentSchoolYear
      )
      .subscribe((_) => {
        this.schoolYearRepo.setSelectedSchoolYear(this.currentSchoolYear);
        AppEvents.schoolYearChanged.emit();

        this.userService.getUserAdministrativeUnits().subscribe((r) => {
          this.userAdministrativeUnits = r;
        });
      });
  }

  // Mobile dropdown focus handlers — open the embedded mat-select inside a
  // mat-menu item when the menu item itself is clicked or activated via keyboard.
  focusSchoolYearSelect(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget as HTMLElement;
    const selectElement = button.querySelector(".mat-mdc-select-trigger") as HTMLElement;
    if (selectElement) {
      setTimeout(() => selectElement.click(), 50);
    }
  }

  focusDistrictSelect(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget as HTMLElement;
    const selectElement = button.querySelector(".mat-mdc-select-trigger") as HTMLElement;
    if (selectElement) {
      setTimeout(() => selectElement.click(), 50);
    }
  }

  // Prevent mat-menu from swallowing arrow/enter/space when the user is
  // navigating the embedded mat-select.
  handleSelectKeydown(event: KeyboardEvent) {
    if (
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.stopPropagation();
    }
  }
}
